import { useEffect, useMemo, useRef } from 'react';

import { graphql } from '@/__generated__';
import {
  AccountSearchQueryFilter,
  AccountSearchV2Query,
  SearchFieldType,
} from '@/__generated__/graphql';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';
import { AppliedSearchParams } from './accountSearch';

/**
 * Valid SearchFieldType values for runtime validation
 */
const VALID_SEARCH_FIELD_TYPES = new Set<string>(Object.values(SearchFieldType));

/**
 * Type guard to validate that highlights data matches the expected structure.
 * The GraphQL schema returns highlights as JSONObject (any), but we expect
 * Partial<Record<SearchFieldType, string[]>>.
 *
 * @param highlights - The raw highlights data from GraphQL
 * @returns Validated highlights object or undefined if invalid
 */
const validateHighlights = (
  highlights: unknown,
): Partial<Record<SearchFieldType, string[]>> | undefined => {
  if (highlights === null || highlights === undefined) {
    return undefined;
  }

  if (typeof highlights !== 'object' || Array.isArray(highlights)) {
    return undefined;
  }

  const validated: Partial<Record<SearchFieldType, string[]>> = {};

  for (const [key, value] of Object.entries(highlights)) {
    // Validate key is a valid SearchFieldType
    if (!VALID_SEARCH_FIELD_TYPES.has(key)) {
      continue;
    }

    // Validate value is an array of strings
    if (!Array.isArray(value)) {
      continue;
    }

    const validStrings = value.filter((item): item is string => {
      if (typeof item !== 'string') {
        return false;
      }
      return item.trim().length > 0;
    });

    if (validStrings.length > 0) {
      validated[key as SearchFieldType] = validStrings;
    }
  }

  return Object.keys(validated).length > 0 ? validated : undefined;
};

export const GetAccountSearchResultsV2 = graphql(/* GraphQL */ `
  query AccountSearchV2(
    $accountId: BigInt!
    $filter: AccountSearchQueryFilter!
    $first: Int!
    $after: String
  ) {
    catalog(accountId: $accountId) {
      searchv2(first: $first, filter: $filter, after: $after) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            hit {
              warehouseAsset {
                accountId
                credentialsId
                name
                relation
                urn
                warehouseType
                type
                connectionName
              }
              dbtAsset {
                resourceType
                __typename
                environmentId
                environmentDeploymentType
                projectId
                cloudProjectName
                uniqueId
                ... on ModelSearchNode {
                  modelHealthIssues: healthIssues
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on SourceSearchNode {
                  sourceHealthIssues: healthIssues
                  snapshottedAt
                  fqn
                  __typename
                }
                ... on ExposureSearchNode {
                  exposureHealthIssues: healthIssues
                  __typename
                }
                ... on SnapshotSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on SeedSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on TestSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
              }
              highlight
              highlights
              matchedField
              resultName
              numCols
              __typename
            }
          }
        }
        aggregations {
          platform
          projects
          environmentType
          assetType
          tags
          modelingLayers
          warehouseAssetTypes
          modelContracts
          dbtMaterializationTypes
        }
      }
    }
  }
`);

const getAccountSearchResultsV2 = (data: AccountSearchV2Query) => {
  return data.catalog.searchv2.edges.map((edge) => edge.node).filter(truthy);
};

const getPageInfo = (data: AccountSearchV2Query) => {
  const { totalCount, pageInfo, aggregations } = data.catalog.searchv2 ?? {
    totalCount: null,
    pageInfo: null,
    aggregations: null,
  };
  return {
    totalCount: totalCount ?? null,
    endCursor: pageInfo?.endCursor ?? null,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    metadata: { aggregations },
  };
};

/**
 * Normalizes the filter to ensure no empty arrays are sent to the GraphQL API.
 * GraphQL schema requires: "Cannot be null or empty array" for array fields.
 * This function omits array fields that are empty or null, including only populated arrays.
 */
const normalizeFilter = (filter: AccountSearchQueryFilter): AccountSearchQueryFilter =>
  Object.fromEntries(
    Object.entries(filter).filter(([_, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null;
    }),
  ) as AccountSearchQueryFilter;

export const useAccountSearchResultsV2 = ({
  accountId = 0,
  filter,
  pageSize = 50,
  after,
  pause,
}: AppliedSearchParams) => {
  const query = usePaginatedQuery(
    {
      query: GetAccountSearchResultsV2,
      variables: {
        accountId,
        filter: normalizeFilter(filter),
        first: pageSize,
        after,
      },
      pause: pause || !accountId || !filter.fields.length,
    },
    getPageInfo,
    getAccountSearchResultsV2,
    () => null,
  );

  const isLoadingMore = query.fetching;

  // Track the filter that produced the current data so we can detect when
  // a new search has been submitted but results haven't arrived yet.
  const filterKey = JSON.stringify(filter);
  const loadedFilterKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.fetching && query.data != null) {
      loadedFilterKeyRef.current = filterKey;
    }
  }, [query.fetching, query.data, filterKey]);

  const isNewSearch = loadedFilterKeyRef.current !== filterKey;
  const isInitialLoad = query.fetching && (query.data == null || isNewSearch);

  return useMemo(() => {
    return {
      ...query,
      data:
        query.data && !isNewSearch
          ? query.data.map((d) => ({
              ...d,
              hit: {
                ...d.hit,
                highlights: validateHighlights(d.hit.highlights),
              },
            }))
          : undefined,
      aggregations: query.pageInfo?.metadata?.aggregations,
      isLoadingMore,
      isInitialLoad,
    };
  }, [query, isLoadingMore, isInitialLoad, isNewSearch]);
};
